const LEVEL_COLUMNS = [

  { level: 1, field: 'state', defaultHeader: 'State' },

  { level: 2, field: 'depot', defaultHeader: 'Depot' },

  { level: 3, field: 'area', defaultHeader: 'Area' },

  { level: 4, field: 'qtr', defaultHeader: 'Qtr No' },

];



function typeLevel(row) {

  const n = Number(row?.location_type_level ?? row?.location_type?.level);

  return Number.isFinite(n) && n > 0 ? n : 0;

}



/** Map location_types.id → { level, code, name } (from dropdown or list API). */

export function buildLocationTypeMetaMap(locationTypes = []) {

  const map = new Map();

  for (const t of locationTypes || []) {

    const id = Number(t.value ?? t.id ?? t.location_type_id);

    if (!Number.isFinite(id) || id <= 0) continue;

    const level = Number(t.level);

    map.set(id, {

      level: Number.isFinite(level) && level > 0 ? level : 0,

      code: String(t.type_code || t.code || '').toUpperCase(),

      name: t.type_name || t.label || t.name || '',

    });

  }

  return map;

}



export function applyLocationTypeMeta(row, typeMap) {

  if (!row || !typeMap?.size) return row;

  const typeId = Number(row.location_type_id);

  const meta = typeMap.get(typeId);

  if (!meta) return row;

  return {

    ...row,

    location_type_level: typeLevel(row) || meta.level || row.location_type_level,

    location_type_code: row.location_type_code || meta.code || '',

    location_type_name: row.location_type_name || meta.name || '',

  };

}



function fieldKeyForNode(node) {

  const level = typeLevel(node);

  const col = LEVEL_COLUMNS.find((c) => c.level === level);

  if (col) return col.field;



  const code = String(

    node?.location_type_code || node?.location_type?.type_code || '',

  ).toUpperCase();

  if (code.includes('STATE')) return 'state';

  if (code.includes('DEPOT')) return 'depot';

  if (code.includes('AREA')) return 'area';

  if (code.includes('QTR')) return 'qtr';

  return null;

}



function buildIndexMap(locations) {

  const byId = new Map();

  for (const loc of locations || []) {

    if (loc?.id != null) byId.set(Number(loc.id), loc);

  }

  return byId;

}



function walkChainFromRoot(id, byId) {

  const chain = [];

  let current = byId.get(Number(id));

  const guard = new Set();



  while (current && !guard.has(Number(current.id))) {

    guard.add(Number(current.id));

    chain.unshift(current);

    const parentId = current.parent_id;

    current =

      parentId != null && parentId !== '' ? byId.get(Number(parentId)) : null;

  }

  return chain;

}



function resolveHierarchyForRow(row, byId) {

  const fields = { state: null, depot: null, area: null, qtr: null };

  const selfLevel = typeLevel(row);

  const rowId = Number(row.id);

  const chain = walkChainFromRoot(row.id, byId);



  for (const node of chain) {

    const key = fieldKeyForNode(node);

    if (!key || !node.name) continue;

    const level = typeLevel(node);

    const isSelf = Number(node.id) === rowId;



    if (isSelf) {

      fields[key] = node.name;

    } else if (!selfLevel || level < selfLevel) {

      fields[key] = node.name;

    }

  }



  const selfKey = fieldKeyForNode(row);

  if (selfKey && row.name && row.name !== '—') {

    fields[selfKey] = row.name;

  }



  return {

    state: fields.state || '—',

    depot: fields.depot || '—',

    area: fields.area || '—',

    qtr: fields.qtr || '—',

  };

}



/**

 * @param {object[]} rows — active location rows (normalized)

 * @param {{ locationTypes?: object[] }} config

 * @param {object[]} allRows — full index including inactive parents

 */

export function enrichLocationsWithHierarchy(rows, config = {}, allRows = []) {

  const typeMap = buildLocationTypeMetaMap(config.locationTypes);

  const index = (allRows?.length ? allRows : rows).map((r) =>

    applyLocationTypeMeta(r, typeMap),

  );

  const byId = buildIndexMap(index);



  return (rows || []).map((row) => {

    const typedRow = applyLocationTypeMeta(row, typeMap);

    const hierarchyFields = resolveHierarchyForRow(typedRow, byId);

    const self = byId.get(Number(typedRow.id)) || typedRow;

    const level = typeLevel(self);

    const typeName =

      typedRow.location_type_name ||

      self.location_type?.type_name ||

      self.location_type?.name ||

      '';



    const hierarchyPath = [

      hierarchyFields.state,

      hierarchyFields.depot,

      hierarchyFields.area,

      hierarchyFields.qtr,

    ]

      .filter((v) => v && v !== '—')

      .join(' › ');



    return {

      ...typedRow,

      ...hierarchyFields,

      hierarchyPath,

      typeLabel: typeName || '—',

      location_type_level: level || typedRow.location_type_level,

      parent_name:

        typedRow.parent_id != null

          ? byId.get(Number(typedRow.parent_id))?.name ?? '—'

          : '—',

    };

  });

}



/** Column field keys are always state / depot / area / qtr so they match enriched rows. */

export function getHierarchyColumnDefs(locationTypes = []) {

  const sorted = [...(locationTypes || [])].sort(

    (a, b) => Number(a.level ?? 0) - Number(b.level ?? 0),

  );



  return LEVEL_COLUMNS.map((defaultCol) => {

    const typeAtLevel = sorted.find((t) => Number(t.level) === defaultCol.level);

    return {

      field: defaultCol.field,

      header:

        typeAtLevel?.type_name ||

        typeAtLevel?.label ||

        typeAtLevel?.name ||

        defaultCol.defaultHeader,

      level: defaultCol.level,

    };

  });

}



export function sortLocationRows(rows) {

  return [...(rows || [])].sort((a, b) => {

    const projectA = Number(a.project_id) || 0;

    const projectB = Number(b.project_id) || 0;

    if (projectA !== projectB) return projectA - projectB;



    const parts = ['state', 'depot', 'area', 'qtr', 'name'];

    for (const key of parts) {

      const va = String(a[key] || '').toLowerCase();

      const vb = String(b[key] || '').toLowerCase();

      if (va < vb) return -1;

      if (va > vb) return 1;

    }

    return (Number(a.id) || 0) - (Number(b.id) || 0);

  });

}



export function getProjectLabelMap(user) {

  const map = {};

  const projects = user?.projects;

  if (!Array.isArray(projects)) return map;

  projects.forEach((p, i) => {

    const id = String(p.id ?? p.project_id ?? p.projectId ?? `p-${i}`);

    const label = String(p.name ?? p.project_name ?? p.label ?? `Project ${id}`).trim();

    map[id] = label || `Project ${id}`;

  });

  return map;

}

